import { reactive } from 'vue'
import { storeToRefs } from 'pinia'
import analytics, {
	Gender,
	MeasurementSystem,
	OnboardingScreenType,
	eventInt,
	eventDecimal,
	eventTimestamp,
	toMedicationName,
} from '@/modules/analytics'
import { featureFlags } from '@/modules/Onboarding/runtime/external/featureFlags'
import { useUserFlagsStore } from '@/store/userFlags'

// Renderer/legacy strings → MeasurementSystem proto enum.
// Schema v0.1.4 expects measurement_system as sint64 (1 = METRIC, 2 = IMPERIAL).
const MEASUREMENT_SYSTEM_MAP = {
	metric: MeasurementSystem.METRIC,
	imperial: MeasurementSystem.IMPERIAL,
}

// Config answer value → Gender proto enum (MALE=1, FEMALE=2).  The enum has no
// prefer_not_to_say / non_binary members on purpose: per DEV-34827 the event is
// NOT sent when the user declined to pick a binary sex.
const GENDER_MAP = {
	male: Gender.MALE,
	female: Gender.FEMALE,
}

// Single source of truth for screen-type values is the SDK enum
// (QUESTION=1, LOADER=2, CONFIRM=3, PAYWALL=4, OTHER=5).  Re-exporting as STEP_TYPE
// keeps existing imports (LandingView.vue, this file's internals) working unchanged.
// Note: OTHER changed from 4 (legacy local) to 5 (lib).  The legacy 4 conflicted with
// the new PAYWALL=4 — silently mis-tagging OTHER events as PAYWALL in Snowflake.
const STEP_TYPE = OnboardingScreenType

// Builder canvas screens carry stepType as a string; the wire enum is numeric.
const STEP_TYPE_BY_NAME = {
	question: STEP_TYPE.QUESTION,
	loader: STEP_TYPE.LOADER,
	confirm: STEP_TYPE.CONFIRM,
	other: STEP_TYPE.OTHER,
}

const QUESTION_COMPONENT_TYPES = new Set([
	'listInput',
	'checkbox',
	'textInput',
	'dateInput',
	'numberInput',
	'imageInput',
	'rateInput',
	'voteInput',
	'tagsInput',
])

// Canonical button names for OnboardingButtonClick event
const BUTTON_NAME = {
	PRIVACY_POLICY: 'privacy_policy',
	TERMS_OF_USE: 'terms_of_use',
}

// href pattern → button_name for DOM-delegated OnboardingButtonClick tracking.
// Only links whose href matches one of these patterns trigger the event.
const LINK_HREF_TO_BUTTON = [
	{ pattern: /\/privacy/, name: BUTTON_NAME.PRIVACY_POLICY },
	{ pattern: /\/tos\b|\/terms/, name: BUTTON_NAME.TERMS_OF_USE },
]

// Screen-specific Jet event names (from PR #2, request_id=821).
// Events in this set are forwarded to Jet via jetScreenSpecificEvent().
const JET_SCREEN_EVENTS = new Set([
	'OnboardingWelcomeDone',
	'OnboardingAgeRangeSelectionDone',
	'OnboardingHeightSelectionDone',
	'OnboardingWeightSelectionDone',
	'OnboardingGoalWeightSelectionDone',
	'OnboardingEmailSubmitDone',
	'OnboardingHealthProblemRiskDone',
	'OnboardingGenderSelectionDone',
])

// Legacy CMS analyticsKey → Jet screen-specific event name + value property key.
// The renderer fires these via onHandleComponentEvents (onUnmounted) with { data: componentValue }.
// handleCustomAnalyticEvent in OnboardingView uses this map to forward data to jetScreenSpecificEvent.
const LEGACY_TO_JET_EVENT = {
	OnboardingHeightScreenDone: { jetName: 'OnboardingHeightSelectionDone', valueKey: 'height' },
	OnboardingMedWeightScreenDone: { jetName: 'OnboardingWeightSelectionDone', valueKey: 'weight' },
	OnboardingMedHappyWeightScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
}

export { STEP_TYPE, JET_SCREEN_EVENTS, LEGACY_TO_JET_EVENT }

// ─── Pure helpers (no state/store dependency) ────────────────────────────────

function formatTitle(name) {
	if (!name) return ''
	return name
		.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
		.replace(/[-\s]+/g, '_')
		.toUpperCase()
		.replace(/^ONBOARDING_/, '')
		.replace(/_SCREEN$/, '')
		.replace(/_DONE$/, '')
}

function jetIsConditional(screen) {
	const conditions = screen?.navigationParams?.conditionsForShow ?? []
	return conditions.some((c) => !(c.variableName === 'featureKey' && /^web_/.test(c.value)))
}

// Partner screens are titled with a partner prefix — Telegra uses `TG_`.
// (CareValidate `CR_` is a deprecated partner and is not used.)
function jetIsPartnerScreen(title) {
	return /^TG_/.test(title || '')
}

function jetComputeStepType(screen) {
	const explicit = screen?.stepType
	if (typeof explicit === 'number') return explicit
	if (typeof explicit === 'string' && STEP_TYPE_BY_NAME[explicit] != null) return STEP_TYPE_BY_NAME[explicit]

	const allComponents = [...(screen?.components ?? []), ...Object.values(screen?.bottomBlock ?? {})]
	const ids = allComponents.map((c) => c.id)

	if (ids.some((id) => QUESTION_COMPONENT_TYPES.has(id))) return STEP_TYPE.QUESTION
	if (ids.includes('button')) return STEP_TYPE.CONFIRM
	if (allComponents.some((c) => c.id === 'custom' && /loader/i.test(c.name))) return STEP_TYPE.LOADER

	return STEP_TYPE.OTHER
}

function jetGenerateStepId(title) {
	const ts = Date.now()
	const str = `${window.deviceId || ''}${title}${ts}`
	let hash = 5381
	for (let i = 0; i < str.length; i++) {
		hash = (((hash << 5) + hash) ^ str.charCodeAt(i)) >>> 0
	}
	return hash.toString(16) + ts.toString(36)
}

function jetComputeSecondsOnScreen(showTimestamp) {
	return showTimestamp ? Math.ceil((Date.now() - showTimestamp) / 1000) : null
}

function jetResolveButtonNameFromHref(href) {
	if (!href) return null
	for (const { pattern, name } of LINK_HREF_TO_BUTTON) {
		if (pattern.test(href)) return name
	}
	return null
}

// ─── Composable ──────────────────────────────────────────────────────────────

const JET_STEP_STATE_KEY = 'jet_last_step'

function persistStepState(stepId, title) {
	try {
		sessionStorage.setItem(JET_STEP_STATE_KEY, JSON.stringify({ stepId, title }))
	} catch {
		/* ignore */
	}
}

function readPersistedStepState() {
	try {
		return JSON.parse(sessionStorage.getItem(JET_STEP_STATE_KEY)) || null
	} catch {
		return null
	}
}

// Hoisted to module scope so generic components (BlockVisibilityObserver,
// OnboardingButtonClick fires, etc.) can read the "current step" without each
// composable consumer producing its own reactive instance.  Only one step can
// be active at a time across the app — Landing → Onboarding → Paywall — so a
// shared singleton matches the runtime contract.
const _persistedStep = readPersistedStepState()
const stepEvents = reactive({
	currentStepId: _persistedStep?.stepId || null,
	previousStepId: null,
	currentTitle: _persistedStep?.title || '',
	previousTitle: '',
	showTimestamp: null,
	isBackwards: false,
	isConditional: false,
	isControl: true,
	isVariation: false,
	isPartnerScreen: false,
	stepType: STEP_TYPE.OTHER,
	lastAnswerData: null,
	lastSecondsOnScreen: null,
})

export { stepEvents }

// Advance the step context: shift current → previous, derive the new current from `name`,
// persist, and return the resulting step props (snake_case, matching the analytics schema;
// empty values omitted). Decoupled from any specific event so any consumer (pre-paywall
// page, paywall, …) can register itself as a step and reuse the same id/title bookkeeping.
export function commitStep(name) {
	const title = formatTitle(name)
	const stepId = jetGenerateStepId(title)

	stepEvents.previousStepId = stepEvents.currentStepId
	stepEvents.previousTitle = stepEvents.currentTitle
	stepEvents.currentStepId = stepId
	stepEvents.currentTitle = title

	persistStepState(stepId, title)

	return {
		...(stepId && { onboarding_step_id: stepId }),
		...(stepEvents.previousStepId && { previous_onboarding_step_id: stepEvents.previousStepId }),
		...(title && { onboarding_title: title }),
		...(stepEvents.previousTitle && { previous_onboarding_title: stepEvents.previousTitle }),
	}
}

// OnboardingRadioButtonClick — fired on a non-navigation / non-answer radio or
// consent-checkbox tap (plan picker, health-data / T&C consent, renewal agreement).
// `buttonName` carries the option identity; reads the shared step context.
// `titleOverride` lets a call-site pin the title (e.g. checkout renewal → 'CHECKOUT').
// `automaticSelection` is TRUE only when the platform picked the option for the user.
export function jetRadioButtonClick(buttonName, titleOverride, automaticSelection = false) {
	const title = titleOverride || stepEvents.currentTitle
	analytics.track('OnboardingRadioButtonClick', {
		...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
		...(title && { onboarding_title: title }),
		...(buttonName && { button_name: buttonName }),
		automatic_selection: automaticSelection,
	})
}

// OnboardingMedicationChoiceDone — the user settled on a treatment (glp-dm Step 1).
// `choice` is the funnel's medication kind; the schema field takes the MedicationName enum.
export function jetMedicationChoiceDone(choice) {
	const medication = toMedicationName(choice)
	analytics.track('OnboardingMedicationChoiceDone', {
		...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
		...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
		...(medication != null && { medication }),
	})
}

// OnboardingAlertDone — fired when a user-facing error / alert appears (input
// validation errors, consent / renewal not accepted, …). `data` is the value the
// user entered, or omitted (NULL per schema) for policy-not-accepted alerts.
export function jetAlertDone(alertDescription, data, titleOverride) {
	const title = titleOverride || stepEvents.currentTitle
	analytics.track('OnboardingAlertDone', {
		...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
		...(title && { onboarding_title: title }),
		...(alertDescription && { alert_description: alertDescription }),
		...(data != null && { data }),
	})
}

// Derive step props for a transient in-screen sub-step (e.g. an options overlay shown within
// one renderer screen) WITHOUT mutating the shared step chain. The renderer owns stepEvents
// for the parent screen and fires its own StepShow/StepDone off it, so committing here would
// clobber that bookkeeping. Instead the sub-step gets a fresh id and links back to the current
// step (the parent screen) as its previous_*, leaving the shared chain untouched.
export function deriveSubStep(name) {
	const title = formatTitle(name)
	const stepId = jetGenerateStepId(title)

	return {
		...(stepId && { onboarding_step_id: stepId }),
		...(stepEvents.currentStepId && { previous_onboarding_step_id: stepEvents.currentStepId }),
		...(title && { onboarding_title: title }),
		...(stepEvents.currentTitle && { previous_onboarding_title: stepEvents.currentTitle }),
	}
}

export function useOnboardingStepEvents() {
	const { getFlags: getUserFlags } = storeToRefs(useUserFlagsStore())

	// ─── Store-dependent helpers ─────────────────────────────────────────

	// A `featureKey` condition is only an experiment when nothing resolves it
	// locally — otherwise the screen is control for everyone who sees it.
	function jetIsExperimentCondition(value) {
		if (value in featureFlags) return false
		if (value === getUserFlags.value?.local?.funnelFeatureFlag) return false
		return true
	}

	function jetIsControl(screen) {
		const conditions = screen?.navigationParams?.conditionsForShow ?? []
		return !conditions.some((c) => c.variableName === 'featureKey' && jetIsExperimentCondition(c.value))
	}

	// ─── Public API ─────────────────────────────────────────────────────

	function jetStepShow(screen, isBackwards, isVariation) {
		const isConditional = jetIsConditional(screen)
		const isControl = jetIsControl(screen)
		const stepType = jetComputeStepType(screen)

		// Advance the shared step context (shift current → previous, persist) and reuse
		// the resulting props for the event payload.
		const stepProps = commitStep(screen?.name || '')
		const isPartnerScreen = jetIsPartnerScreen(stepProps.onboarding_title)

		stepEvents.showTimestamp = Date.now()
		stepEvents.isBackwards = isBackwards
		stepEvents.isConditional = isConditional
		stepEvents.isControl = isControl
		stepEvents.isVariation = !!isVariation
		stepEvents.isPartnerScreen = isPartnerScreen
		stepEvents.stepType = stepType
		stepEvents.lastAnswerData = null

		analytics.track('OnboardingStepShow', {
			...stepProps,
			is_backwards: !!isBackwards,
			is_control: !!isControl,
			is_conditional: !!isConditional,
			is_variation: !!isVariation,
			is_partner_screen: isPartnerScreen,
			...(stepType != null && { step_type: stepType }),
		})

		return stepProps
	}

	function jetStepDone(isStepBack, dataStringOverride) {
		if (!stepEvents.showTimestamp) return

		const secondsOnScreen = jetComputeSecondsOnScreen(stepEvents.showTimestamp)

		stepEvents.lastSecondsOnScreen = secondsOnScreen

		// Per spec 8.15: when user leaves via back, data_* must be NULL.
		// dataStringOverride lets call-sites (e.g. LandingView quiz answer) inject the
		// answer directly without going through jetCaptureAnswer / lastAnswerData.
		const answer = isStepBack ? {} : stepEvents.lastAnswerData || {}
		const dataString = isStepBack ? null : (dataStringOverride ?? answer.data_string)

		analytics.track('OnboardingStepDone', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.previousStepId && { previous_onboarding_step_id: stepEvents.previousStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(stepEvents.previousTitle && { previous_onboarding_title: stepEvents.previousTitle }),
			is_backwards: !!stepEvents.isBackwards,
			is_control: !!stepEvents.isControl,
			is_conditional: !!stepEvents.isConditional,
			is_variation: !!stepEvents.isVariation,
			is_partner_screen: !!stepEvents.isPartnerScreen,
			...(stepEvents.stepType != null && { step_type: stepEvents.stepType }),
			...(secondsOnScreen !== null && { seconds_on_screen: eventInt(secondsOnScreen) }),
			is_step_back: !!isStepBack,
			...(dataString != null && { data_string: dataString }),
			...(answer.data_float != null && { data_float: eventDecimal(String(answer.data_float)) }),
			...(answer.data_int != null && { data_int: eventInt(answer.data_int) }),
			// data_array is `repeated string` in proto — the encoder requires an iterable,
			// not undefined, even when the field is logically unset.
			data_array: Array.isArray(answer.data_array) ? answer.data_array : [],
		})

		// One StepShow → at most one StepDone. Clearing the timestamp makes a second
		// jetStepDone (without an intervening jetStepShow) no-op on the guard above —
		// e.g. the landing step otherwise gets closed twice: once by LandingView.goNext,
		// then again by OnboardingView's first @screen-changed when the builder mounts.
		stepEvents.showTimestamp = null
	}

	function jetButtonClick(buttonName, blockName) {
		analytics.track('OnboardingButtonClick', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(buttonName && { button_name: buttonName }),
			...(blockName && { block_name: blockName }),
		})
	}

	function jetHandleDelegatedClick(event) {
		const el = event.target.closest('a')
		if (!el) return

		const buttonName = jetResolveButtonNameFromHref(el.href)
		if (buttonName) {
			// Delegated <a> clicks (privacy / terms) don't have a structural block context —
			// leave block_name unset.  Schema allows the field to be optional.
			jetButtonClick(buttonName)
		}
	}

	/**
	 * Forwards a renderer-emitted screen-specific event to Jet.
	 * Adds onboardingStepId automatically. onboardingTitle is NOT added —
	 * only a few events have it in their schema (e.g. OnboardingHealthProblemRiskDone).
	 * Callers must pass onboardingTitle explicitly in properties when needed.
	 * Unknown events (not in JET_SCREEN_EVENTS) are silently ignored.
	 */
	function jetScreenSpecificEvent(eventName, properties = {}) {
		if (!JET_SCREEN_EVENTS.has(eventName)) return

		// OnboardingWelcomeDone — migrated to med-analytics-ts (priority 2 in Romanov's list).
		// Bypasses the Jet Long/Decimal conversion entirely — analytics.track takes plain numbers.
		// secondsOnScreen falls back to lastSecondsOnScreen captured by jetStepDone, because
		// the renderer emits @screen-changed (→ jetStepDone) before @event-triggered (→ here).
		if (eventName === 'OnboardingWelcomeDone') {
			const payload = {}
			if (stepEvents.currentStepId) payload.onboarding_step_id = stepEvents.currentStepId
			const secs = properties?.secondsOnScreen ?? stepEvents.lastSecondsOnScreen
			if (secs != null) payload.seconds_on_screen = eventInt(secs)
			if (properties?.entryPoint) payload.entry_point = properties.entryPoint
			analytics.track('OnboardingWelcomeDone', payload)
			return
		}

		// Priority 4: age / height / weight / goal-weight selection events.
		// Proto schema v0.1.4 stores height/weight as `string`, measurement_system as MeasurementSystem enum.
		if (eventName === 'OnboardingAgeRangeSelectionDone') {
			const payload = {}
			const stepId = properties?.onboardingStepId ?? stepEvents.currentStepId
			if (stepId) payload.onboarding_step_id = stepId
			if (properties?.onboardingTitle) payload.onboarding_title = properties.onboardingTitle
			if (properties?.ageRange != null) payload.age_range = String(properties.ageRange)
			if (properties?.date != null) {
				payload.date = eventTimestamp(
					properties.date instanceof Date ? properties.date : new Date(Number(properties.date)),
				)
			}
			analytics.track('OnboardingAgeRangeSelectionDone', payload)
			return
		}

		if (
			eventName === 'OnboardingHeightSelectionDone' ||
			eventName === 'OnboardingWeightSelectionDone' ||
			eventName === 'OnboardingGoalWeightSelectionDone'
		) {
			const valueKey = eventName === 'OnboardingHeightSelectionDone' ? 'height' : 'weight'
			const payload = {}
			const stepId = properties?.onboardingStepId ?? stepEvents.currentStepId
			if (stepId) payload.onboarding_step_id = stepId
			if (properties?.onboardingTitle) payload.onboarding_title = properties.onboardingTitle
			if (properties?.[valueKey] != null) payload[valueKey] = eventDecimal(String(properties[valueKey]))
			const ms = MEASUREMENT_SYSTEM_MAP[properties?.measurementSystem]
			if (ms != null) payload.measurement_system = ms
			if (properties?.realData) payload.real_data = properties.realData
			analytics.track(eventName, payload)
			return
		}

		// OnboardingGenderSelectionDone (DEV-34827) — fired when the user picks a binary sex
		// on a `variableName: "sex"` screen (MED_GENDER, and its GENDER follow-up).  A
		// prefer_not_to_say / non_binary answer has no Gender enum member and must NOT
		// produce an event — hence the early return on an unmapped value.
		if (eventName === 'OnboardingGenderSelectionDone') {
			const gender = GENDER_MAP[properties?.gender]
			if (gender == null) return

			const payload = { gender }
			const stepId = properties?.onboardingStepId ?? stepEvents.currentStepId
			if (stepId) payload.onboarding_step_id = stepId
			const title = properties?.onboardingTitle ?? stepEvents.currentTitle
			if (title) payload.onboarding_title = title
			analytics.track('OnboardingGenderSelectionDone', payload)
			return
		}

		// OnboardingHealthProblemRiskDone — fired when a health-blocker modal is shown.
		// problem_type is treated as RED-field by legal but allowed on client per privacy review.
		if (eventName === 'OnboardingHealthProblemRiskDone') {
			const payload = {}
			const stepId = properties?.onboardingStepId ?? stepEvents.currentStepId
			if (stepId) payload.onboarding_step_id = stepId
			if (properties?.onboardingTitle) payload.onboarding_title = properties.onboardingTitle
			if (properties?.problemType) payload.problem_type = properties.problemType
			analytics.track('OnboardingHealthProblemRiskDone', payload)
		}
	}

	// Called from surveyEventHandler when a *ScreenNameDone* event fires
	function jetCaptureAnswer(data) {
		const raw = data?.properties?.data
		if (raw === undefined || raw === null) return

		// Measurement screens (weight/height/target) emit { formValue, value, unitSystem };
		// the numeric `value` is the answer → data_float (decimal, covers whole + fractional).
		if (typeof raw === 'object' && !Array.isArray(raw)) {
			const num = Number(raw.value)
			stepEvents.lastAnswerData = Number.isFinite(num) ? { data_float: num } : raw
			return
		}

		if (Array.isArray(raw)) {
			// Single choice → data_string; multi-select → data_array.
			const values = [...raw].map(String)
			stepEvents.lastAnswerData = values.length === 1 ? { data_string: values[0] } : { data_array: values.sort() }
		} else if (typeof raw === 'number') {
			if (Number.isInteger(raw)) {
				stepEvents.lastAnswerData = { data_int: raw }
			} else {
				stepEvents.lastAnswerData = { data_float: raw }
			}
		} else if (typeof raw === 'string') {
			stepEvents.lastAnswerData = { data_string: raw }
		}
	}

	return {
		stepEvents,
		jetStepShow,
		jetStepDone,
		jetButtonClick,
		jetHandleDelegatedClick,
		jetScreenSpecificEvent,
		jetCaptureAnswer,
	}
}
